import React, { useState } from 'react';
import { Modal } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { user } from '../../assets/images/images';
import { useLazyGetDogByIDQuery } from '../../services/dogsApi';
import DogByIDModal from '../dogByIDModal/dogByIDModal';
import EditDogModal from '../editDogModal/editDogModal';
import EditUserModal from '../editUserModal/editUserModal';
import { sortDogsByName } from '../../helpers/helpers';

const UserDetailModal = ({ isOpen, onClose, data, onRefresh }) => {
  const [getDogByID, { isLoading: fetchingDog }] = useLazyGetDogByIDQuery();
  const [selectedDog, setSelectedDog] = useState(null);
  const [isDogModalOpen, setIsDogModalOpen] = useState(false);
  const [isEditDogModalOpen, setIsEditDogModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);

  const handleDogClick = async dogId => {
    const response = await getDogByID(dogId);
    setSelectedDog(response?.data?.data);
    setIsDogModalOpen(true);
  };

  const handleEditDog = () => {
    setIsDogModalOpen(false);
    setIsEditDogModalOpen(true);
  };

  const handleCloseDogModal = () => {
    setIsDogModalOpen(false);
    setSelectedDog(null);
  };

  const handleCloseEditDogModal = () => {
    setIsEditDogModalOpen(false);
    setSelectedDog(null);
  };

  const handleEditSuccess = () => {
    onRefresh?.();
  };

  const handleEditUser = () => {
    setIsEditUserModalOpen(true);
  };

  const handleCloseEditUserModal = () => {
    setIsEditUserModalOpen(false);
  };

  const handleEditUserSuccess = () => {
    onRefresh?.();
  };

  return (
    <Modal
      width={600}
      footer={false}
      open={isOpen}
      onCancel={onClose}
      style={{
        top: 20,
      }}
    >
      <div className="my-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img
              src={data?.user?.avatar || user}
              className="w-16 h-16 rounded-full object-cover"
              alt="avatar"
            />
            <div className="flex flex-col ml-2">
              <p className="text-xl font-semibold text-themeBlack">
                {data?.user?.full_name}
              </p>
              <a
                href={`mailto:${data?.user?.email}`}
                className="text-themeGray-dark hover:text-primary transition-colors cursor-pointer"
              >
                {data?.user?.email}
              </a>
              <div className="flex items-center gap-2 mt-1">
                {data?.user?.active === false ? (
                  <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded">
                    Disabled
                  </span>
                ) : (
                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-600 rounded">
                    Active
                  </span>
                )}
                <button
                  onClick={handleEditUser}
                  className="p-1 text-gray-400 hover:text-primary hover:bg-gray-100 rounded transition-colors"
                  title="Edit user"
                >
                  <EditOutlined />
                </button>
              </div>
            </div>
          </div>
          {data?.totalUnpaidPrice > 0 ? (
            <div className="flex flex-col items-end bg-red-50 border border-red-200 rounded-lg px-4 py-2">
              <p className="text-sm text-red-600 font-medium">
                Total Unpaid Amount
              </p>
              <p className="text-2xl font-bold text-red-600">
                ${(data?.totalUnpaidPrice || 0).toFixed(2)}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-end bg-green-50 border border-green-200 rounded-lg px-4 py-2">
              <p className="text-sm text-green-600 font-medium">Balance</p>
              <p className="text-2xl font-bold text-green-600">$0.00</p>
            </div>
          )}
        </div>
        <div className="flex items-center mt-6">
          <div className="flex flex-col w-5/12">
            <p className="text-lg text-primary font-semibold">
              Internal Username:
            </p>
            <p className="text-lg">{data?.user?.username}</p>
          </div>
          <div className="flex flex-col w-5/12">
            <p className="text-lg text-primary font-semibold">Contact:</p>
            {data?.user?.contact ? (
              <a
                href={`tel:${data.user.contact}`}
                className="text-lg text-themeGray-dark hover:text-primary transition-colors"
              >
                {data.user.contact}
              </a>
            ) : (
              <p className="text-lg">---</p>
            )}
          </div>
        </div>
        <div className="flex flex-col mt-4">
          <p className="text-lg text-primary font-semibold">Address:</p>
          <div className="text-lg">
            {data?.user?.address?.street && <p>{data.user.address.street}</p>}
            {(data?.user?.address?.city ||
              data?.user?.address?.state ||
              data?.user?.address?.zipCode) && (
              <p>
                {[
                  data?.user?.address?.city,
                  data?.user?.address?.state,
                  data?.user?.address?.zipCode,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col mt-4">
          <p className="text-lg text-primary font-semibold">
            Secondary Contact:
          </p>
          <div className="text-lg flex flex-col">
            <span>{data?.user?.secondary_contact?.name || '---'}</span>
            {data?.user?.secondary_contact?.contact ? (
              <a
                href={`tel:${data.user.secondary_contact.contact}`}
                className="text-base text-themeGray-dark hover:text-primary transition-colors"
              >
                {data.user.secondary_contact.contact}
              </a>
            ) : (
              <span className="text-base">---</span>
            )}
          </div>
        </div>
        <div className="flex flex-col mt-6">
          <p className="text-lg text-primary font-semibold mb-2">
            Dogs ({data?.dogs?.length || 0})
          </p>
          {data?.dogs?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {sortDogsByName(data.dogs).map(dog => (
                <div
                  key={dog._id}
                  className="flex items-center p-2 bg-gray-50 rounded-lg border border-gray-100 cursor-pointer hover:bg-gray-100 hover:border-primary transition-all"
                  onClick={() => handleDogClick(dog._id)}
                >
                  <img
                    src={dog.avatar}
                    alt={dog.dog_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="font-medium text-themeBlack truncate hover:text-primary">
                      {dog.dog_name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {dog.breed || 'Unknown breed'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400">No dogs registered</p>
          )}
        </div>
      </div>

      <DogByIDModal
        isOpen={isDogModalOpen}
        onClose={handleCloseDogModal}
        data={selectedDog}
        isLoading={fetchingDog}
        onEditDog={handleEditDog}
        zIndex={1010}
      />

      <EditDogModal
        isOpen={isEditDogModalOpen}
        onClose={handleCloseEditDogModal}
        data={selectedDog}
        onSuccess={handleEditSuccess}
        zIndex={1010}
      />

      <EditUserModal
        isOpen={isEditUserModalOpen}
        onClose={handleCloseEditUserModal}
        data={data?.user}
        onSuccess={handleEditUserSuccess}
        zIndex={1010}
      />
    </Modal>
  );
};

export default UserDetailModal;
